import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import algoliasearch, { SearchClient, SearchIndex } from "algoliasearch";
import useAuth from "../hooks/useAuth";
import { WEBOT_SERVER_ENDPOINT } from "logic/Consts";
import { isPgSearchEnabled } from "utils/listingsSearchApi";

// Refresh the secured key this many seconds before its validUntil. Keeps
// the in-flight window comfortably inside the validity window.
const REFRESH_LEAD_SECONDS = 5 * 60;

// If a proactive refresh fails (network blip, throttled background tab, a
// briefly-expired Firebase token), re-arm a short retry instead of giving up.
// Without this the refresh loop dead-ends and the key silently expires.
const PROACTIVE_RETRY_MS = 30 * 1000;

type SecuredKeyResponse = {
  appId: string;
  indexName: string;
  securedApiKey: string;
  validUntil: number;
};

type AlgoliaClientContextValue = {
  client: SearchClient | null;
  index: SearchIndex | null;
  validUntil: number | null;
  refresh: () => Promise<void>;
};

const AlgoliaClientContext = createContext<AlgoliaClientContextValue>({
  client: null,
  index: null,
  validUntil: null,
  refresh: async () => {}
});

type AlgoliaClientProviderProps = {
  children: ReactNode;
};

// A secured key lasts ~1h. Whenever Algolia rejects a search because the key
// expired or is otherwise invalid (HTTP 401/403), we must mint a fresh key and
// retry silently rather than surfacing "no results" to the customer. Network /
// host failures (RetryError) are NOT key problems and must not trigger a re-mint.
function isExpiredOrInvalidKeyError(err: unknown): boolean {
  const status = (err as { status?: unknown })?.status;
  if (status === 401 || status === 403) return true;
  const message = String((err as { message?: unknown })?.message ?? "").toLowerCase();
  // An EXPIRED secured key does NOT come back as 401/403 — Algolia returns
  // HTTP 400 with a message mentioning the key / `validUntil`. If we don't
  // recognize it here the silent re-mint never fires and the search resolves
  // to an empty list, which the UI shows as "no areas". Match generously: any
  // of these phrasings means "the key is the problem, mint a fresh one".
  return (
    message.includes("api key") ||
    message.includes("apikey") ||
    message.includes("securedapikey") ||
    message.includes("not authorized") ||
    message.includes("validuntil") ||
    message.includes("valid until") ||
    (message.includes("key") && message.includes("expired")) ||
    (message.includes("key") && message.includes("invalid")) ||
    (message.includes("key") && message.includes("no longer valid"))
  );
}

// ---------------------------------------------------------------------------
// localStorage persistence for the secured key.
// ---------------------------------------------------------------------------
// The key is search-only and scoped to the signed-in user, so persisting it is
// safe and lets the dropdown work on the FIRST keystroke after a reload instead
// of waiting on a network mint. We always background-refresh on top of a
// hydrated key, so the stored value keeps getting replaced with a newer one.
const STORAGE_KEY_PREFIX = "webot.algolia.securedKey.";

function readPersistedKey(userId: string): SecuredKeyResponse | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY_PREFIX + userId);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as SecuredKeyResponse;
    // Only hydrate a key that's still comfortably valid — reuse the same lead
    // window so we never adopt one that's about to expire.
    const nowSeconds = Math.floor(Date.now() / 1000);
    if (!parsed?.securedApiKey || !parsed.validUntil || parsed.validUntil - REFRESH_LEAD_SECONDS <= nowSeconds) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function persistKey(userId: string, body: SecuredKeyResponse): void {
  try {
    window.localStorage.setItem(STORAGE_KEY_PREFIX + userId, JSON.stringify(body));
  } catch {
    // localStorage can be unavailable (private mode / quota). Non-fatal: the
    // in-memory key still works, we just lose cross-reload persistence.
  }
}

// Wraps a live Algolia index behind a stable object whose search calls
// self-heal: on an expired/invalid-key error they mint a fresh key (via
// `mintFresh`) and retry exactly once. `getIndex` always returns the freshest
// real index, so we never close over a stale key.
function createResilientIndex(
  getIndex: () => SearchIndex | null,
  mintFresh: () => Promise<SearchIndex>
): SearchIndex {
  const callWithRetry = async (method: "search" | "searchForFacetValues", args: unknown[]) => {
    let index = getIndex();
    if (!index) index = await mintFresh();
    try {
      return await (index as any)[method](...args);
    } catch (err) {
      if (!isExpiredOrInvalidKeyError(err)) throw err;
      // The key expired mid-session. Mint a new one and retry once, silently.
      const fresh = await mintFresh();
      return await (fresh as any)[method](...args);
    }
  };

  return new Proxy({} as SearchIndex, {
    get(_target, prop) {
      if (prop === "search" || prop === "searchForFacetValues") {
        return (...args: unknown[]) => callWithRetry(prop, args);
      }
      // Any other property/method falls through to the live index.
      const index = getIndex();
      return index ? (index as any)[prop] : undefined;
    }
  });
}

export function AlgoliaClientProvider({ children }: AlgoliaClientProviderProps) {
  const { user } = useAuth();
  const [state, setState] = useState<{
    client: SearchClient | null;
    index: SearchIndex | null;
    validUntil: number | null;
  }>({ client: null, index: null, validUntil: null });

  // Live, mutable view of the latest key/index. The resilient wrapper and the
  // proactive scheduler read these refs so they always act on the freshest key.
  const userRef = useRef(user);
  userRef.current = user;
  const indexRef = useRef<SearchIndex | null>(null);
  const validUntilRef = useRef<number | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mintInFlightRef = useRef<Promise<SearchIndex> | null>(null);
  const scheduleProactiveRefreshRef = useRef<() => void>(() => {});
  const retryMintSoonRef = useRef<() => void>(() => {});

  // Install a key body into the live refs + state. Shared by the network mint
  // and the localStorage hydration path so both go through one code path.
  const installKey = useCallback((body: SecuredKeyResponse): SearchIndex => {
    const client = algoliasearch(body.appId, body.securedApiKey);
    const index = client.initIndex(body.indexName);
    indexRef.current = index;
    validUntilRef.current = body.validUntil;
    // Drives readiness (null -> ready) and re-arms the proactive timer.
    setState({ client, index, validUntil: body.validUntil });
    return index;
  }, []);

  // Fetch a brand-new secured key and install it. Concurrent callers (e.g. a
  // batch of parallel searches that all hit an expired key) share one in-flight
  // request so we mint exactly once.
  const mintFreshIndex = useCallback(async (): Promise<SearchIndex> => {
    const currentUser = userRef.current;
    if (!currentUser?.id) throw new Error("AlgoliaClientContext: no authenticated user to mint a secured key for");
    if (mintInFlightRef.current) return mintInFlightRef.current;

    const inFlight = (async () => {
      const idToken = await currentUser.getIdToken();
      const res = await fetch(`${WEBOT_SERVER_ENDPOINT}/api/v1/algolia/secured-key`, {
        method: "GET",
        headers: { Authorization: idToken }
      });
      if (!res.ok) {
        throw new Error(`Failed to fetch Algolia secured key: ${res.status} ${res.statusText}`);
      }
      const body = (await res.json()) as SecuredKeyResponse;
      const index = installKey(body);
      // Persist so a reload (and the next session) starts from a working key
      // instead of a blank dropdown. Each successful mint overwrites the prior
      // stored value — the key is constantly replaced with the newest version.
      if (currentUser.id) persistKey(currentUser.id, body);
      return index;
    })();

    mintInFlightRef.current = inFlight;
    try {
      return await inFlight;
    } finally {
      mintInFlightRef.current = null;
    }
  }, [installKey]);

  // Schedule the next proactive refresh ~5 min before expiry. Re-arms itself on
  // failure (short retry) so a single failed refresh can't dead-end the loop.
  const scheduleProactiveRefresh = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    const validUntil = validUntilRef.current;
    if (!validUntil) return;
    let delayMs = (validUntil - REFRESH_LEAD_SECONDS) * 1000 - Date.now();
    if (delayMs <= 0) delayMs = PROACTIVE_RETRY_MS;
    timerRef.current = setTimeout(() => {
      // On success, setState bumps validUntil and the effect re-arms with the
      // fresh expiry. On failure, re-attempt the mint itself (not just
      // re-schedule) so an expired key can't dead-end the loop.
      mintFreshIndex().catch(() => {
        retryMintSoonRef.current();
      });
    }, delayMs);
  }, [mintFreshIndex]);
  scheduleProactiveRefreshRef.current = scheduleProactiveRefresh;

  // Re-arm a retry that actually RE-ATTEMPTS the mint. Used after a failed
  // initial fetch or a failed proactive refresh: in those states `validUntil`
  // can still be null, so scheduleProactiveRefresh() short-circuits on its
  // `if (!validUntil) return` guard and the recovery loop dead-ends — leaving
  // the user with no index (and therefore no address autocomplete) for the
  // entire session. This keeps re-minting until one succeeds; on success
  // setState bumps validUntil and the normal proactive refresh takes back over.
  const retryMintSoon = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      mintFreshIndex().catch(() => retryMintSoonRef.current());
    }, PROACTIVE_RETRY_MS);
  }, [mintFreshIndex]);
  retryMintSoonRef.current = retryMintSoon;

  // Stable resilient index. Built once; reads the freshest key via indexRef and
  // self-heals via mintFreshIndex. Stable identity keeps consumer effects (whose
  // deps include the index) from needlessly re-running on every key rotation.
  const resilientIndexRef = useRef<SearchIndex | null>(null);
  if (!resilientIndexRef.current) {
    resilientIndexRef.current = createResilientIndex(() => indexRef.current, () => mintFreshIndex());
  }
  const resilientIndex = resilientIndexRef.current;

  // Initial fetch when the user becomes available; reset on sign-out.
  useEffect(() => {
    // A secured key is a browser-side credential over the ENTIRE scraped listing
    // corpus — seller phone numbers included — which is the exposure the Postgres
    // migration exists to close. When search runs server-side no key may be minted
    // at all: no initial fetch, no localStorage hydration, no refresh timers.
    if (isPgSearchEnabled()) return;

    if (!user?.id) {
      indexRef.current = null;
      validUntilRef.current = null;
      if (timerRef.current) clearTimeout(timerRef.current);
      setState({ client: null, index: null, validUntil: null });
      return;
    }
    // Hydrate instantly from a previously-persisted key so address autocomplete
    // works on the FIRST keystroke after a reload — no waiting on the network
    // mint. We still refresh in the background below, so a hydrated key is only
    // ever a head start, never the final word.
    const persisted = readPersistedKey(user.id);
    if (persisted) installKey(persisted);

    mintFreshIndex().catch((err) => {
      // Don't dead-end on a failed initial fetch. If we hydrated above, the
      // dropdown already works and this is just a background rotation that will
      // retry. If we didn't, `validUntil` is still null, so
      // scheduleProactiveRefresh() would no-op — we must re-attempt the mint
      // itself, repeatedly, until one succeeds. Otherwise a single transient
      // failure (network blip, getIdToken() race, momentary 401/500, throttled
      // background tab) silently kills autocomplete for the whole session.
      console.error("AlgoliaClientContext: initial secured-key fetch failed", err);
      retryMintSoonRef.current();
    });
  }, [user?.id, mintFreshIndex, installKey]);

  // Re-arm the proactive refresh whenever a fresh key is installed.
  useEffect(() => {
    if (!state.validUntil) return;
    scheduleProactiveRefresh();
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [state.validUntil, scheduleProactiveRefresh]);

  const value = useMemo<AlgoliaClientContextValue>(
    () => ({
      client: state.client,
      // Expose the self-healing index as soon as there's an authenticated user
      // — NOT only after the first key mint succeeds. The resilient wrapper
      // mints a key on demand inside the first `.search()` call, so consumers
      // (autocomplete, filters) work on the very first keystroke even if the
      // initial background fetch hasn't landed (or failed). Gating this on
      // `state.index` was the bug that left users with a permanently empty
      // dropdown after a single transient key-fetch failure.
      //
      // In Postgres mode the index stays null: the resilient wrapper mints a key
      // on demand inside `.search()`, so handing it out would re-open the very
      // exposure this migration closes. Every consumer either skips its Algolia
      // guard or takes a server-side path in that mode.
      index: user?.id && !isPgSearchEnabled() ? resilientIndex : null,
      validUntil: state.validUntil,
      refresh: () =>
        isPgSearchEnabled()
          ? Promise.resolve()
          : mintFreshIndex()
              .then(() => {})
              .catch((err) => {
                console.error("AlgoliaClientContext: failed to refresh secured key", err);
              })
    }),
    [user?.id, state.client, state.validUntil, resilientIndex, mintFreshIndex]
  );

  return <AlgoliaClientContext.Provider value={value}>{children}</AlgoliaClientContext.Provider>;
}

export function useAlgoliaClient(): AlgoliaClientContextValue {
  return useContext(AlgoliaClientContext);
}

// Convenience hook for the common "I just want the index" case. Returns a
// self-healing index whenever there's an authenticated user — even before the
// first key has been minted: the first `.search()` mints a key on demand and
// retries silently, and an expired key mid-session is replaced transparently.
// Returns null only when there's no signed-in user. Callers should still guard
// for null to cover the signed-out case.
export function useAlgoliaIndex(): SearchIndex | null {
  return useContext(AlgoliaClientContext).index;
}
